import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import { type GridColDef, type GridRowsProp } from '@mui/x-data-grid';
import { type FC, useMemo } from 'react';

import { useCamerasForPatient } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Camera } from '../../models/index.js';
import CameraIrToggleButton from '../Camera/CameraIrToggleButton.js';
import CameraVisionSnippetsButton from '../Camera/CameraVisionSnippetsButton.js';
import CameraZonesButton from '../Camera/CameraZonesButton.js';
import { CameraForm, CameraDelete } from '../Camera/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

interface PatientCamerasTableProps {
  patientId?: string;
  title?: string;
}

const PatientCamerasTable: FC<PatientCamerasTableProps> = ({ patientId, title }) => {
  const { data = [], refetch, isLoading } = useCamerasForPatient(patientId);

  const {open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen} = useDialogControls<Camera>(data);
  const {open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete} = useDialogControls<Camera>(data);

  const { canCreateCamera, canUpdateCamera, canDeleteCamera, canManageVisionDebug } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'ipAddress', headerName: 'IP Address', flex: 1 },
      { field: 'port', headerName: 'Port', flex: 1 },
      {
        field: 'zones',
        headerName: 'Zones',
        width: 140,
        sortable: false,
        renderCell: (params) => (
          <CameraZonesButton
            cameraId={params.row.id as string}
            cameraName={params.row.name as string}
            zones={params.row.zones as Camera['zones']}
          />
        ),
      },
    ];
    if (canUpdateCamera) {
      cols.push({
        field: 'ir',
        headerName: 'IR light',
        width: 90,
        sortable: false,
        // Axis-only — NVR-attached cameras (Reolink) don't speak VAPIX.
        renderCell: (params) =>
          params.row.isAxis ? (
            <CameraIrToggleButton
              cameraId={params.row.id as string}
              irCutFilter={params.row.irCutFilter as boolean | null | undefined}
              irIlluminator={params.row.irIlluminator as boolean | null | undefined}
            />
          ) : null,
      });
    }
    if (canManageVisionDebug) {
      cols.push({
        field: 'vision',
        headerName: 'Vision',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <CameraVisionSnippetsButton cameraId={params.row.id as string} cameraName={params.row.name as string} />
        ),
      });
    }
    if (canUpdateCamera || canDeleteCamera) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canUpdateCamera,
          canDelete: canDeleteCamera,
        }),
      });
    }
    return cols;
  }, [canUpdateCamera, canDeleteCamera, canManageVisionDebug, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(
    () =>
      data.map((item: Camera) => ({
        id: item.id,
        name: item.name,
        ipAddress: item.ipAddress,
        port: item.port,
        zones: item.zones ?? [],
        isAxis: !item.nvrId && !item.channel,
        irCutFilter: item.irCutFilter,
        irIlluminator: item.irIlluminator,
      })) || [],
    [data],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ mb: 1.5 }}>
        {title ? (
          <Typography variant={'h5'} component={'h3'}>
            {title}
          </Typography>
        ) : null}
        {canCreateCamera ? (
          <Button
            variant={'contained'}
            size={'small'}
            startIcon={<AddIcon />}
            aria-label={'Add a new camera'}
            onClick={() => setFormOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Add camera
          </Button>
        ) : null}
      </Stack>
      <Table
        columns={columns}
        rows={rows}
        pageSize={10}
        NoRowsOverlayText={'No cameras have been connected to this profile.'}
        isLoading={isLoading}
      />
      <CameraForm
        patientId={patientId}
        open={formOpen}
        onClose={resetForm}
        camera={formData}
        refetch={refetch}
      />
      <CameraDelete
        open={deleteOpen}
        onClose={resetDelete}
        camera={deleteData}
        refetch={refetch}
      />
    </Box>
  );
};

export default PatientCamerasTable;
